import { AppAlertDialog } from '../../../../shared/general';
import { FormElementDynamicInputTyped } from '../../../../shared/form-elements';
import { PrivacyFormFields, PrivacyFormRequest } from '../types';
import { Button, Image, Link } from '@chakra-ui/react';
import { getDefaultValues, getExplainRequestType, prepareFormData } from '../utils';
import { isValidEmail, isValidZipcode } from '../../../../../utils/validations';
import { validationMessages } from '../../../../shared/validation/validation-messages';
import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { usePrivacyFormFieldsConfig } from '../config';
import { FormContext } from '../../../../../context';
import { API } from '../../../../../services';
import { assetPrefix, scrollTo } from '../../../../../utils';
import { typesLang } from '../../../../../@types/shared/types';

export const PrivacyFormAllSections: React.FunctionComponent = () => {
  const intl = useIntl();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid, isDirty }
  } = useForm<PrivacyFormFields>({
    mode: 'onBlur',
    defaultValues: getDefaultValues(intl)
  });

  const privacyFormFieldsConfig = usePrivacyFormFieldsConfig(intl);
  const { isLoading, hasError, setLoading, setError } = useContext(FormContext);
  const values = watch();
  const [activeStep, setActiveStep] = useState<1 | 2>(1);
  const [isEnterPressed, setIsEnterPressed] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.code === 'Enter') {
      setIsEnterPressed(true);
    }
  };

  async function onSubmit(values: PrivacyFormFields) {
    if (isEnterPressed) {
      setIsEnterPressed(false);
      return;
    }
    setLoading(true);
    try {
      await API.post<{ url: string }, PrivacyFormRequest>(
        'privacyform/sendprivacyform',
        prepareFormData(values, intl.locale as typesLang)
      );
      setActiveStep(2);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
      scrollTo('.react-tools');
    }
  }

  const Success = () => (
    <div hidden={activeStep === 2 ? false : true} className={`min-h-[400px]`}>
      <div className={`flex flex-col gap-10 md:flex-row md:gap-20`}>
        <div className={`md:w-1/2 `}>
          <FormattedMessage
            id={`privacyForm.successMessage`}
            defaultMessage={`Bedankt voor het indienen van jouw verzoek. Je ontvangt een bevestiging hiervan op het door
        jouw opgegeven e-mailadres. Wij nemen jouw verzoek in behandeling en zullen binnen één maand
        reageren.`}
          />
        </div>
        <Image
          className={`md:w-1/2 w-full`}
          src={`${assetPrefix}/assets/forms/privacy/images/thank-you.jpg`}></Image>
      </div>
    </div>
  );

  return (
    <>
      <AppAlertDialog title={'let op!'} open={hasError} onClose={() => setError(false)}>
        <div>
          <FormattedMessage
            id={`privacyForm.error`}
            defaultMessage={`Door een technische storing kunnen we nu helaas het formulier niet versturen.
          Probeer het opnieuw.`}
          />
        </div>
      </AppAlertDialog>
      <Success />
      <form
        hidden={activeStep === 1 ? false : true}
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={handleKeyDown}>
        <div>
          <h1>
            <FormattedMessage
              id={`privacyForm.title`}
              defaultMessage={`Verzoek Privacy verwerking persoonsgegevens`}
            />
          </h1>
          <p>
            <FormattedMessage
              id={`privacyForm.subTitle`}
              defaultMessage={`De Hypotheker gebruikt jouw persoonsgegevens om je van dienst te kunnen zijn. We gaan
            hier heel zorgvuldig mee om. Uiteraard blijf je zelf eigenaar van deze informatie. Wil
            je gebruik maken van jouw rechten met betrekking tot je persoonsgegevens, vul dan
            onderstaand formulier in.`}
            />
          </p>
        </div>
        <div className={`mt-4 mb-8`}>
          <FormattedMessage
            id={`privacyForm.privacyStatement`}
            defaultMessage={`Ga voor meer informatie ten aanzien van jouw rechten naar het`}
          />{' '}
          <Link href={`https://www.hypotheker.nl/privacy-statement`} isExternal>
            <FormattedMessage
              id={`privacyForm.privacyStatement.link.label`}
              defaultMessage={`Privacy Statement`}
            />
          </Link>
          .
        </div>
        <div className={`mb-8`}>
          <h3>
            <FormattedMessage id={`privacyForm.section.request.title`} defaultMessage={`Verzoek`} />
          </h3>
          <FormElementDynamicInputTyped<PrivacyFormFields>
            formFields={privacyFormFieldsConfig}
            id={'requestType'}
            register={register}
            errors={errors}
            values={values}
            options={{ required: true }}
          />

          {values.requestType && (
            <>
              <div
                className={`bg-blue-50 p-4 mt-4 italic`}
                dangerouslySetInnerHTML={{
                  __html: getExplainRequestType(values.requestType, intl) || ''
                }}></div>
              {values.requestType === 'RectificatiePersoonsgegevens' && (
                <FormElementDynamicInputTyped<PrivacyFormFields>
                  formFields={privacyFormFieldsConfig}
                  id={'requestReason'}
                  register={register}
                  errors={errors}
                  values={values}
                  options={{ required: true }}
                />
              )}
            </>
          )}
        </div>

        <div className={`my-8`}>
          <h3>
            <FormattedMessage
              id={`privacyForm.section.details.title`}
              defaultMessage={`Persoonlijke gegevens`}
            />
          </h3>
          <div className={`grid grid-cols-2 gap-4`}>
            <FormElementDynamicInputTyped<PrivacyFormFields>
              formFields={privacyFormFieldsConfig}
              id={'firstname'}
              register={register}
              errors={errors}
              values={values}
              options={{ required: true }}
            />
            <FormElementDynamicInputTyped<PrivacyFormFields>
              formFields={privacyFormFieldsConfig}
              id={'lastname'}
              register={register}
              errors={errors}
              values={values}
              options={{ required: true }}
            />
          </div>
          <div className={`grid grid-cols-6 gap-4`}>
            <div className="col-span-6 md:col-span-4">
              <FormElementDynamicInputTyped<PrivacyFormFields>
                formFields={privacyFormFieldsConfig}
                id={'street'}
                register={register}
                errors={errors}
                values={values}
                options={{ required: true }}
              />
            </div>
            <div className={`col-span-3 md:col-span-1`}>
              <FormElementDynamicInputTyped<PrivacyFormFields>
                formFields={privacyFormFieldsConfig}
                id={'houseNumber'}
                register={register}
                errors={errors}
                values={values}
                options={{ required: true }}
              />
            </div>
            <div className={`col-span-3 md:col-span-1`}>
              <FormElementDynamicInputTyped<PrivacyFormFields>
                formFields={privacyFormFieldsConfig}
                id={'houseNumberAddition'}
                register={register}
                errors={errors}
                values={values}
              />
            </div>
          </div>
          <div className={`grid grid-cols-3 gap-4`}>
            <div className="col-span-1">
              <FormElementDynamicInputTyped<PrivacyFormFields>
                formFields={privacyFormFieldsConfig}
                id={'postalCode'}
                register={register}
                errors={errors}
                values={values}
                validation={{
                  validate: (value: string) => isValidZipcode(value, validationMessages.zipCode),
                  required: validationMessages.required
                }}
              />
            </div>
            <div className="col-span-2">
              <FormElementDynamicInputTyped<PrivacyFormFields>
                formFields={privacyFormFieldsConfig}
                id={'city'}
                register={register}
                errors={errors}
                values={values}
                options={{ required: true }}
              />
            </div>
          </div>
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-4`}>
            <FormElementDynamicInputTyped<PrivacyFormFields>
              formFields={privacyFormFieldsConfig}
              id={'emailKnown'}
              register={register}
              validation={{
                validate: (value: string) => isValidEmail(value, validationMessages.email),
                required: validationMessages.required
              }}
              errors={errors}
              values={values}
            />
            {isValidEmail(values.emailKnown) && values.emailKnownNotContact === 'nee' && (
              <FormElementDynamicInputTyped<PrivacyFormFields>
                formFields={privacyFormFieldsConfig}
                id={'emailContact'}
                register={register}
                validation={{
                  validate: (value: string) => isValidEmail(value, validationMessages.email),
                  required: validationMessages.required
                }}
                errors={errors}
                values={values}
              />
            )}
          </div>
          {isValidEmail(values.emailKnown) && (
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-4 my-4`}>
              <FormElementDynamicInputTyped<PrivacyFormFields>
                formFields={privacyFormFieldsConfig}
                id={'emailKnownNotContact'}
                register={register}
                errors={errors}
                values={values}
                options={{ required: true }}
              />
            </div>
          )}
        </div>
        <Button
          isLoading={isLoading}
          loadingText={intl.formatMessage({
            id: 'privacyForm.submitButton.loadingText',
            defaultMessage: 'Bezig met versturen'
          })}
          type="submit"
          colorScheme="orange"
          isDisabled={false}>
          {!isValid && isDirty
            ? intl.formatMessage({
                id: 'privacyForm.submitButton.whatElse',
                defaultMessage: 'Wat moet ik nog invullen?'
              })
            : intl.formatMessage({
                id: 'privacyForm.submitButton.label',
                defaultMessage: 'Versturen'
              })}
        </Button>
      </form>
    </>
  );
};
